import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapPin } from 'lucide-react';
import { api } from '@/lib/api';
import { cn, numero } from '@/lib/utils';
import { EstadoErro } from '@/componentes/ui/estados';
import { Esqueleto } from '@/componentes/ui/primitivos';
import { Botao, codigoClicavel } from '@/componentes/ui/botao';
import { useSessao } from '@/hooks/useSessao';
import { DialogoAlocacao, type EtiquetaParaAlocar } from './DialogoAlocacao';

/**
 * A lista inteira de uma pendência, com rolagem própria.
 *
 * Rola SÓ ela, com teto de altura: são até centenas de linhas, e deixar a
 * página crescer faria o painel de pendências — que é o resumo — subir para
 * fora da tela assim que alguém abrisse uma.
 *
 * A DATA É A PRIMEIRA COLUNA, e vem com "há N dias" ao lado: numa fila de
 * pendência a pergunta nunca é "quando chegou", é "está parado há quanto
 * tempo". Por isso a ordem também é da mais recente para a mais antiga, e não
 * por número.
 *
 * O QUE É UMA LINHA MUDA COM A PENDÊNCIA
 * --------------------------------------
 * Em "etiquetas sem alocação" a linha é a ETIQUETA — produto, sequência e
 * código de barras —, porque é isso que alguém vai procurar no chão. Nas
 * demais a linha é o documento: o recebimento, ou a nota de compra.
 */

export type ChavePendencia =
  | 'notaSemRecebimento'
  | 'semConferencia'
  | 'semAprovacao'
  | 'semEtiqueta'
  | 'semAlocacao';

export interface LinhaPendencia {
  numero: number;
  data: string | null;
  descricao: string | null;
  produto: number | null;
  codigoBarras: string | null;
  sequencia: number | null;
  quantidade: number | null;
}

function dataCurta(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

/** Há quantos dias está parado — a leitura que importa numa fila. */
function parado(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dias = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  return dias <= 0 ? 'hoje' : dias === 1 ? 'há 1 dia' : `há ${dias} dias`;
}

export function GavetaPendencia({
  empresa,
  centro,
  chave,
  total,
  aoAbrirRecebimento,
  aoFechar,
}: {
  empresa: number;
  /** O centro de destino da alocação — só ele oferece posições. */
  centro: number | null;
  chave: ChavePendencia;
  total: number;
  /** `null` para nota de compra: o recebimento dela ainda não existe. */
  aoAbrirRecebimento: ((recebimento: number) => void) | null;
  aoFechar: () => void;
}): JSX.Element {
  const { temPermissao } = useSessao();
  const podeAlocar = temPermissao('recebimento.alocar');
  const [alocando, definirAlocando] = React.useState<EtiquetaParaAlocar | null>(null);
  const consulta = useQuery<{ linhas: LinhaPendencia[] }>({
    queryKey: ['recebimento-pendencias', 'detalhe', empresa, chave],
    queryFn: () =>
      api.get<{ linhas: LinhaPendencia[] }>(`/api/recebimentos/pendencias/${chave}`, {
        empresa,
        dias: 60,
      }),
    staleTime: 60_000,
  });

  const linhas = consulta.data?.linhas ?? [];
  const porEtiqueta = chave === 'semAlocacao';
  const rotuloDoc = chave === 'notaSemRecebimento' ? 'Nota' : 'Recebimento';

  return (
    <div className="relative mt-2 rounded-xl border border-borda bg-superficie2">
      <div className="flex items-center gap-2 border-b border-borda px-3 py-1.5">
        <span className="text-[11.5px] font-bold">
          {consulta.isLoading ? 'Carregando…' : `${numero(linhas.length)} de ${numero(total)}`}
        </span>
        {!consulta.isLoading && linhas.length < total ? (
          <span className="text-[11px] text-textoFraco">mostrando as mais recentes</span>
        ) : null}
        <button
          type="button"
          onClick={aoFechar}
          className="ml-auto text-[11.5px] font-semibold text-textoFraco hover:text-texto"
        >
          fechar
        </button>
      </div>

      {consulta.isLoading ? (
        <Esqueleto className="m-3 h-24" />
      ) : consulta.isError ? (
        <div className="p-3">
          <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
        </div>
      ) : (
        <div className="max-h-[340px] overflow-y-auto">
          {/* `table-fixed` + `max-w-0` na descrição: é o que faz a descrição
              ceder espaço em vez de empurrar o botão para fora da tela. */}
          <table className="w-full table-fixed text-left">
            <thead className="sticky top-0 bg-superficie2">
              <tr className="border-b border-borda text-textoFraco">
                <th className="rotulo w-[74px] px-2 py-1.5">Data</th>
                <th className="rotulo w-[76px] py-1.5 pr-2">{rotuloDoc}</th>
                {porEtiqueta ? <th className="rotulo w-[62px] py-1.5 pr-2">Produto</th> : null}
                <th className="rotulo py-1.5 pr-2">{porEtiqueta ? 'Descrição' : 'Fornecedor'}</th>
                {porEtiqueta ? <th className="rotulo w-[128px] py-1.5 pr-2">Etiqueta</th> : null}
                {porEtiqueta && podeAlocar ? <th className="w-[92px] pb-1.5 pr-2" /> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-borda">
              {linhas.map((l, i) => (
                <tr key={`${l.numero}-${l.codigoBarras ?? i}`}>
                  {/*
                    Data em DUAS linhas, não lado a lado: junto com endereço,
                    produto, descrição e código de barras, a linha passava da
                    largura da gaveta e o botão de alocar — a única coisa
                    acionável ali — ficava atrás de um scroll lateral.
                  */}
                  <td className="whitespace-nowrap px-2 py-1.5 text-[11px] leading-tight">
                    <span className="tabular block font-semibold">{dataCurta(l.data)}</span>
                    <span className="block text-textoFraco">{parado(l.data)}</span>
                  </td>
                  <td className="py-1.5 pr-2">
                    {aoAbrirRecebimento ? (
                      <button
                        type="button"
                        onClick={() => aoAbrirRecebimento(l.numero)}
                        className={cn(codigoClicavel, 'tabular font-mono text-[11.5px] font-bold')}
                      >
                        {l.numero}
                      </button>
                    ) : (
                      <span className="tabular font-mono text-[11.5px]">{l.numero}</span>
                    )}
                  </td>
                  {porEtiqueta ? (
                    <td className="tabular py-1.5 pr-2 font-mono text-[11.5px]">
                      {l.produto ?? '—'}
                    </td>
                  ) : null}
                  <td
                    className="max-w-0 truncate py-1.5 pr-2 text-[11px]"
                    title={l.descricao ?? ''}
                  >
                    {l.descricao ?? '—'}
                  </td>
                  {porEtiqueta ? (
                    <td className="whitespace-nowrap py-1.5 pr-2 text-[10.5px] leading-tight">
                      <span className="tabular block font-mono">{l.codigoBarras ?? '—'}</span>
                      <span className="block text-textoFraco">
                        seq {l.sequencia ?? '—'} · {numero(l.quantidade ?? 0)} un
                      </span>
                    </td>
                  ) : null}
                  {/*
                    Alocar direto daqui: quem está olhando a lista do que falta
                    guardar é exatamente quem vai guardar. Mandá-lo abrir outra
                    tela para agir sobre a linha que já está na frente dele é o
                    tipo de ida e volta que faz a pendência envelhecer.
                  */}
                  {porEtiqueta && podeAlocar ? (
                    <td className="py-1.5 pr-2 text-right">
                      <Botao
                        variante="secundaria"
                        tamanho="pequeno"
                        onClick={() =>
                          definirAlocando({
                            codigoBarras: l.codigoBarras ?? '',
                            recebimento: l.numero,
                            produto: l.produto ?? 0,
                            descricao: l.descricao,
                            quantidade: l.quantidade,
                            sequencia: l.sequencia,
                          })
                        }
                      >
                        <MapPin className="h-3.5 w-3.5" />
                        Alocar
                      </Botao>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DialogoAlocacao
        etiqueta={alocando}
        empresa={empresa}
        centro={centro}
        aoFechar={() => definirAlocando(null)}
        aoConcluir={() => void consulta.refetch()}
      />
    </div>
  );
}
